import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, GraduationCap, Lock } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface SchoolStat {
  school: string;
  class_year: number;
  contributions: number;
  contributors: number;
}

const rankClass = (i: number) =>
  i === 0
    ? "bg-primary text-primary-foreground"
    : i < 3
    ? "bg-primary/15 text-primary"
    : "bg-muted text-muted-foreground";

const LeaderboardPage = () => {
  const { user, loading: authLoading } = useAuth();
  const [rows, setRows] = useState<SchoolStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState("all");
  const [hasReview, setHasReview] = useState<boolean | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setHasReview(false);
      setLoading(false);
      return;
    }
    const load = async () => {
      // reviews_public exposes is_owner instead of user_id (user_id is not readable by clients)
      const { count } = await supabase
        .from("reviews_public" as any)
        .select("id", { count: "exact", head: true })
        .eq("is_owner", true);
      const eligible = (count ?? 0) > 0;
      setHasReview(eligible);

      if (eligible) {
        const { data } = await supabase
          .from("school_stats" as any)
          .select("school, class_year, contributions, contributors")
          .order("contributions", { ascending: false });
        setRows(((data as any[]) ?? []) as SchoolStat[]);
      }
      setLoading(false);
    };
    load();
  }, [user, authLoading]);


  const years = useMemo(
    () =>
      Array.from(new Set(rows.map((r) => r.class_year).filter((y) => y > 0))).sort((a, b) => a - b),
    [rows]
  );

  const ranked = useMemo(() => {
    const filtered = year === "all" ? rows : rows.filter((r) => String(r.class_year) === year);
    const bySchool = new Map<string, SchoolStat>();
    for (const r of filtered) {
      const existing = bySchool.get(r.school);
      if (existing) {
        existing.contributions += r.contributions;
        existing.contributors += r.contributors;
      } else {
        bySchool.set(r.school, { ...r });
      }
    }
    return Array.from(bySchool.values()).sort((a, b) => b.contributions - a.contributions);
  }, [rows, year]);

  const topCount = ranked[0]?.contributions ?? 1;

  if (!authLoading && hasReview === false) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <main className="container max-w-lg py-20 flex-1">
          <div className="rounded-2xl border border-border bg-card p-8 text-center shadow-card">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-6 h-6 text-primary" />
            </div>
            <h1 className="font-serif text-2xl text-foreground mb-2">Contributors only</h1>
            <p className="text-sm text-muted-foreground mb-6">
              {user
                ? "The School Challenge leaderboard unlocks once you've shared your first rotation, residency, fellowship or internship review."
                : "Create an account and post a review to unlock the School Challenge leaderboard."}
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              {user ? (
                <Link to="/rotations">
                  <Button className="rounded-lg w-full sm:w-auto">Write a review</Button>
                </Link>
              ) : (
                <>
                  <Link to="/signup">
                    <Button className="rounded-lg w-full sm:w-auto">Create account</Button>
                  </Link>
                  <Link to="/login">
                    <Button variant="outline" className="rounded-lg w-full sm:w-auto">Sign in</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />

      <div className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-primary/10 to-transparent" />
        <div className="container relative py-10">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
              <Trophy className="w-7 h-7 text-primary" />
            </div>
            <div>
              <h1 className="font-serif text-2xl text-foreground">School Challenge</h1>
              <p className="text-sm text-muted-foreground">
                Every documented experience counts toward your school and class.
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      <main className="container max-w-2xl py-10 flex-1">
        <div className="flex items-center justify-between gap-3 mb-5">
          <h2 className="font-serif text-lg text-foreground flex items-center gap-2">
            <GraduationCap className="w-4 h-4 text-muted-foreground" />
            {year === "all" ? "All classes" : `Class of ${year}`}
          </h2>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-40 rounded-lg">
              <SelectValue placeholder="Class year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All classes</SelectItem>
              {years.map((y) => (
                <SelectItem key={y} value={String(y)}>
                  Class of {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground py-10 text-center">Loading leaderboard…</p>
        ) : ranked.length === 0 ? (
          <div className="rounded-2xl border border-border bg-card p-6 text-center">
            <p className="text-sm text-muted-foreground mb-4">
              No schools on the board yet. Add your school and class year to your profile, then post a review to put it on the map.
            </p>
            <Link to="/profile">
              <Button className="rounded-lg">Set up my profile</Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-2">
            {ranked.map((r, i) => (
              <motion.div
                key={r.school}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="rounded-xl border border-border bg-card p-4 shadow-card"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-xs font-bold ${rankClass(i)}`}>{i + 1}</span>
                    <div className="min-w-0">
                      <p className="font-serif text-base text-card-foreground truncate">{r.school}</p>
                      <p className="text-xs text-muted-foreground">
                        {r.contributors} contributor{r.contributors === 1 ? "" : "s"}
                      </p>
                    </div>
                  </div>
                  <span className="text-sm font-bold text-primary shrink-0">
                    {r.contributions} contribution{r.contributions === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-muted mt-3">
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${Math.max(6, Math.round((r.contributions / topCount) * 100))}%` }}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <p className="text-xs text-muted-foreground mt-6 text-center">
          Only school names and totals are shown — individual reviews always stay anonymous.
        </p>
      </main>
      <Footer />
    </div>
  );
};

export default LeaderboardPage;
